// Albums and collections of an encrypted library (HRTH-164.6, 164.7). Each is an entry of
// its own, its metadata encrypted under the library key like a file's, with no blobs but
// perhaps uploaded cover art.
//
// Albums say what a file is: audio albums (tracks, played as a queue) and visual albums
// (photos and videos, a grid and a viewer). A file is on at most one: it names it
// (`EntryMeta.in`). Music needs no setup: audio on no album is grouped by its tags (artist
// + album) into albums that exist only on screen until someone edits one; that makes a
// record which keeps matching those tags, so tracks uploaded later join it.
//
// Collections (type `files`) are what people put together: any files, in their order
// (`items`), a file in any number of them as well as its album.

import type { LibraryEntry } from '../api/types'
import type { BlobKey, EntryKind, EntryMeta } from './libraryEntries'
import { decryptName, encryptName } from './metacrypto'

export type CollectionType = 'audio' | 'visual' | 'files'

export interface CollectionMeta {
  v: 1
  record: 'collection'
  type: CollectionType
  name: string
  /** Audio albums: who made it. */
  artist?: string
  /** Albums: `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. */
  release_date?: string
  description?: string
  /** A photo of the library shown as the cover (its entry id). */
  cover?: string
  /** Audio albums made from tags: the tracks (on no album) it gathers. */
  match?: { artist: string; album: string }
  /** Collections: their files, in order (entry ids). */
  items?: string[]
  added: number
  /** Uploaded cover art, if any. */
  blobs: { art?: BlobKey }
}

export interface OpenCollection {
  entry: LibraryEntry
  meta: CollectionMeta
}

/** A collection as shown: a record, or an album gathered from tags (`virtual`). */
export interface Collection<T> {
  /** The record's entry id, or `tag:<artist>\0<album>` for an album from tags. */
  id: string
  type: CollectionType
  name: string
  artist?: string
  release_date?: string
  description?: string
  record?: OpenCollection
  items: T[]
}

type FileRow = { entry: LibraryEntry; meta: EntryMeta }

const recordKind = (library: string) => `library-entry:${library}` as const

export function encryptCollection(key: string, library: string, meta: CollectionMeta): Promise<string> {
  return encryptName(key, recordKind(library), JSON.stringify(meta))
}

/** What an entry's metadata decrypts to: a file, a collection, or nothing this key opens. */
export async function decryptRecord(
  key: string,
  library: string,
  entry: LibraryEntry,
): Promise<{ file: EntryMeta } | { collection: CollectionMeta } | null> {
  const json = await decryptName(key, recordKind(library), entry.meta)
  if (!json || json === entry.meta) return null
  try {
    const m = JSON.parse(json) as Partial<EntryMeta> & Partial<CollectionMeta>
    if (m.v !== 1) return null
    if (m.record === 'collection' && typeof m.name === 'string' && (m.type === 'audio' || m.type === 'visual' || m.type === 'files')) {
      return { collection: { ...(m as CollectionMeta), blobs: m.blobs ?? {} } }
    }
    return typeof m.path === 'string' ? { file: m as EntryMeta } : null
  } catch {
    return null
  }
}

export type AlbumType = Exclude<CollectionType, 'files'>

/** Which album types a file can go on (collections take anything). */
export function fits(type: CollectionType, kind: EntryKind): boolean {
  if (type === 'files') return true
  if (type === 'audio') return kind === 'audio'
  return kind === 'image' || kind === 'video'
}

export const TYPE_NAMES: Record<CollectionType, { one: string; new: string; label: string }> = {
  audio: { one: 'album', new: 'New album', label: 'Album' },
  visual: { one: 'album', new: 'New album', label: 'Photo album' },
  files: { one: 'collection', new: 'New collection', label: 'Collection' },
}

// U+241F (␟) joins artist and album: printable, so the id travels in addresses.
const tagKey = (artist: string, album: string) => `${artist}\u241f${album}`
const tagId = (artist: string, album: string) => `tag:${tagKey(artist, album)}`

/** Whether an id is an album gathered from tags (no record yet). */
export const isVirtual = (id: string) => id.startsWith('tag:')

/**
 * Albums with their files and collections with theirs, and the files on no album. A file
 * is on the album it names, if that exists and fits it; audio that names none (`in` unset,
 * not '') joins the album its tags match: a record made from those tags, else one gathered
 * from them. Collections hold the files they list, in order (and, from before they had a
 * list, files that named them).
 */
export function groupCollections<T extends FileRow>(
  files: T[],
  records: OpenCollection[],
): { albums: Collection<T>[]; collections: Collection<T>[]; loose: T[] } {
  const byId = new Map<string, Collection<T>>()
  const byTags = new Map<string, Collection<T>>()
  const curated: Collection<T>[] = []
  const fileById = new Map(files.map((f) => [f.entry.id, f]))
  for (const r of records) {
    const c: Collection<T> = {
      id: r.entry.id,
      type: r.meta.type,
      name: r.meta.name,
      ...(r.meta.artist ? { artist: r.meta.artist } : {}),
      ...(r.meta.release_date ? { release_date: r.meta.release_date } : {}),
      ...(r.meta.description ? { description: r.meta.description } : {}),
      record: r,
      items: [],
    }
    if (r.meta.type === 'files') {
      c.items = [...new Set(r.meta.items ?? [])].flatMap((id) => fileById.get(id) ?? [])
      curated.push(c)
      continue
    }
    byId.set(c.id, c)
    if (r.meta.type === 'audio' && r.meta.match) byTags.set(tagKey(r.meta.match.artist, r.meta.match.album), c)
  }
  const loose: T[] = []
  for (const f of files) {
    const legacy = f.meta.in ? curated.find((c) => c.id === f.meta.in) : undefined
    if (legacy && !legacy.items.includes(f)) legacy.items.push(f)
    const named = f.meta.in ? byId.get(f.meta.in) : undefined
    if (named && fits(named.type, f.meta.kind)) {
      named.items.push(f)
      continue
    }
    const album = f.meta.tags?.album
    if (f.meta.in === undefined && f.meta.kind === 'audio' && album) {
      const artist = f.meta.tags?.artist ?? 'Unknown artist'
      const k = tagKey(artist, album)
      let c = byTags.get(k)
      if (!c) {
        c = { id: tagId(artist, album), type: 'audio', name: album, artist, items: [] }
        byTags.set(k, c)
        byId.set(c.id, c)
      }
      c.items.push(f)
      continue
    }
    loose.push(f)
  }
  // Tracks by number; photos and videos by when they were taken, then name.
  for (const c of byId.values()) {
    if (c.type === 'audio') {
      c.items.sort((x, y) => (x.meta.tags?.track ?? 0) - (y.meta.tags?.track ?? 0) || x.meta.path.localeCompare(y.meta.path))
    } else {
      c.items.sort((x, y) => (x.meta.tags?.taken ?? '').localeCompare(y.meta.tags?.taken ?? '') || x.meta.path.localeCompare(y.meta.path))
    }
  }
  const byName = (a: Collection<T>, b: Collection<T>) => a.name.localeCompare(b.name) || (a.artist ?? '').localeCompare(b.artist ?? '')
  return { albums: [...byId.values()].sort(byName), collections: curated.sort(byName), loose }
}

/** Artists of the music, A–Z: who an album is by, else who a track is by. */
export function artistsOf<T extends FileRow>(albums: Collection<T>[], tracks: T[]): { name: string; albums: Collection<T>[]; tracks: T[] }[] {
  const by = new Map<string, { name: string; albums: Collection<T>[]; tracks: T[] }>()
  const get = (name: string) => {
    if (!by.has(name)) by.set(name, { name, albums: [], tracks: [] })
    return by.get(name)!
  }
  const onAlbum = new Map<T, Collection<T>>()
  for (const a of albums) {
    if (a.type !== 'audio') continue
    for (const t of a.items) onAlbum.set(t, a)
    if (a.items.length || a.artist) get(a.artist ?? 'Unknown artist').albums.push(a)
  }
  for (const t of tracks) {
    if (t.meta.kind !== 'audio') continue
    const a = onAlbum.get(t)
    get(a?.artist ?? t.meta.tags?.artist ?? 'Unknown artist').tracks.push(t)
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** A collection's items with `ids` added at the end (those already in it stay put). */
export function withItems(meta: CollectionMeta, ids: string[]): CollectionMeta {
  const items = [...(meta.items ?? [])]
  for (const id of ids) if (!items.includes(id)) items.push(id)
  return { ...meta, items }
}

/** A collection's items without `ids`. */
export function withoutItems(meta: CollectionMeta, ids: string[]): CollectionMeta {
  return { ...meta, items: (meta.items ?? []).filter((i) => !ids.includes(i)) }
}

/** A collection's items with `id` moved one place up (-1) or down (1). */
export function movedItem(meta: CollectionMeta, id: string, by: -1 | 1): CollectionMeta {
  const items = [...(meta.items ?? [])]
  const at = items.indexOf(id)
  const to = at + by
  if (at < 0 || to < 0 || to >= items.length) return meta
  ;[items[at], items[to]] = [items[to]!, items[at]!]
  return { ...meta, items }
}

/** Where a collection's cover comes from, in order of preference. */
export type Cover<T> = { from: 'record'; record: OpenCollection } | { from: 'art'; file: T } | { from: 'thumb'; file: T } | null

const FOLDER_COVER = /^(cover|folder|front)\.(jpe?g|png|webp)$/i
const dirOf = (path: string) => path.slice(0, path.lastIndexOf('/') + 1)

/**
 * A collection's cover: art uploaded for it, the library photo it names, a cover image in
 * its tracks' folder (cover.jpg, folder.jpg, front.png…), the first embedded art, or the
 * first photo or video's picture.
 */
export function coverOf<T extends FileRow>(c: Collection<T>, files: T[]): Cover<T> {
  if (c.record?.meta.blobs.art && c.record.entry.blobs.some((b) => b.role === 'art')) return { from: 'record', record: c.record }
  const named = c.record?.meta.cover ? files.find((f) => f.entry.id === c.record!.meta.cover && f.meta.kind === 'image') : undefined
  if (named) return { from: 'thumb', file: named }
  if (c.type === 'audio' && c.items.length) {
    const dir = dirOf(c.items[0]!.meta.path)
    const inFolder = files.find((f) => f.meta.kind === 'image' && dirOf(f.meta.path) === dir && FOLDER_COVER.test(f.meta.path.slice(dir.length)))
    if (inFolder) return { from: 'thumb', file: inFolder }
  }
  // Collections show what they start with; albums prefer art, then a photo.
  if (c.type === 'files') {
    const first = c.items.find((f) => f.meta.kind === 'image' || f.meta.blobs.art)
    if (first) return first.meta.kind === 'image' ? { from: 'thumb', file: first } : { from: 'art', file: first }
    return null
  }
  const art = c.items.find((f) => f.meta.blobs.art)
  if (art) return { from: 'art', file: art }
  const photo = c.items.find((f) => f.meta.kind === 'image')
  return photo ? { from: 'thumb', file: photo } : null
}

/** A new collection's metadata; from an album gathered from tags, it keeps matching them. */
export function newCollection(type: CollectionType, name: string, from?: Collection<unknown>): CollectionMeta {
  return {
    v: 1,
    record: 'collection',
    type,
    name: name.slice(0, 200),
    ...(from?.artist ? { artist: from.artist } : {}),
    ...(from && isVirtual(from.id) && from.artist ? { match: { artist: from.artist, album: from.name } } : {}),
    ...(type === 'files' ? { items: [] } : {}),
    added: Date.now(),
    blobs: {},
  }
}
